'use client'

import React, { useState } from 'react'
import { motion } from 'motion/react'

import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { useLenisContext } from '@/providers/Lenis'
import { cn } from '@/utilities/ui'

export type Tab = {
  name: string
  value: string
  icon?: React.ReactNode
}

type TabSelectProps = {
  /** Unique id for this TabSelect instance; used for layoutId so each instance has its own indicator animation. */
  instanceKey?: string
  tabs: Tab[]
  variant?: 'default' | 'evergreen'
  size?: 'small' | 'default'
  mobileSelect?: boolean
  className?: string
  value?: string // Controlled: parent manages state
  defaultValue?: string // Uncontrolled: component manages state
  onTabChange?: (selectedTab: string) => void
  /**
   * Restricts the selected-indicator's layout animation to changes of this
   * value (motion's `layoutDependency`). Pass the selected tab value to keep
   * the indicator from re-animating when the surrounding layout resizes.
   */
  layoutDependency?: unknown
}

const WRAPPER_CLASSES = {
  default: 'bg-background border-border',
  evergreen: 'bg-white border-border',
}

const SELECTED_INDICATOR_CLASSES = {
  default: 'bg-primary',
  evergreen: 'bg-evergreen-800',
}

export const TabSelect: React.FC<TabSelectProps> = (props: TabSelectProps) => {
  const {
    instanceKey,
    tabs,
    variant = 'default',
    size = 'default',
    mobileSelect = false,
    className,
    value,
    defaultValue,
    onTabChange,
    layoutDependency,
  } = props
  const [internalSelectedTab, setInternalSelectedTab] = useState<string | undefined>(defaultValue)
  const { isMobile } = useLenisContext()

  // Use controlled value if provided, otherwise use internal state
  const selectedTab = value !== undefined ? value : internalSelectedTab

  const handleTabChange = (newValue: string) => {
    // Only update internal state if uncontrolled
    if (value === undefined) {
      setInternalSelectedTab(newValue)
    }
    onTabChange?.(newValue)
  }

  return isMobile && mobileSelect ? (
    <NativeSelect
      key={instanceKey ?? `tabs-select-${selectedTab}`}
      value={selectedTab}
      onChange={(e) => handleTabChange(e.target.value)}
    >
      {tabs.map((tab) => (
        <NativeSelectOption key={`tabs-select-option-${tab.value}`} value={tab.value}>
          {tab.name}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  ) : (
    <nav
      key={instanceKey ?? `tabs-select-${selectedTab}`}
      className={cn(
        'h-fit w-full rounded-xl border border-border p-1 font-body',
        WRAPPER_CLASSES[variant],
        className,
      )}
    >
      <ul className={cn('flex w-full gap-1.5', size === 'small' && 'gap-1')} role="tablist">
        {tabs.map((tab) => {
          const isSelected = selectedTab === tab.value

          return (
            <li
              key={`tabs-select-item-${tab.value}`}
              role="tab"
              aria-selected={isSelected}
              className={cn(
                'relative flex w-full items-center justify-center font-body',
                isSelected ? 'selected' : '',
              )}
            >
              {isSelected ? (
                <motion.div
                  key={
                    instanceKey ? `${instanceKey}-indicator` : `tabs-select-indicator-${tab.value}`
                  }
                  layoutId={
                    instanceKey
                      ? `${instanceKey}-selected-indicator`
                      : `tabs-select-selected-indicator`
                  }
                  layoutDependency={layoutDependency}
                  className={cn(
                    'absolute inset-0 z-1 rounded-xl',
                    SELECTED_INDICATOR_CLASSES[variant],
                  )}
                />
              ) : null}
              <motion.button
                key={
                  instanceKey
                    ? `${instanceKey}-button-${tab.value}`
                    : `tabs-select-button-${tab.value}`
                }
                onTapStart={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  handleTabChange(tab.value)
                }}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
                whileTap={{ scale: 0.9 }}
                whileFocus={{
                  backgroundColor: 'hsl(var(--evergreen-500) / 0.2)',
                  outline: '2px solid hsl(var(--evergreen-500) / 0.5)',
                }}
                className={cn(
                  'relative z-2 flex w-full cursor-pointer items-center justify-center rounded-xl border-none bg-transparent px-4 py-1 font-body text-reg font-medium text-foreground focus:outline-none',
                  size === 'small' && 'px-1 py-0.5 text-sm',
                  isSelected && variant === 'evergreen' && 'text-white',
                  isSelected && variant === 'default' && 'text-primary-foreground',
                )}
              >
                {tab.name}
                {tab.icon && (
                  <span className={cn('text-inherit', size === 'small' ? 'ml-1' : 'ml-2')}>
                    {tab.icon}
                  </span>
                )}
              </motion.button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
